import React, { useState } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError } from '../utils/alert';
import './ExpenseForm.css';
import './RevenueDesktop.css';

function BankForm({ bank, onSuccess }) {
  const [formData, setFormData] = useState(
    bank || {
      name: '',
      code: '',
      agency: '',
      account_number: '',
      account_type: 'Corrente',
      initial_balance: 0,
      opening_date: '',
      status: 'Ativa',
      color: '#000000',
      overdraft_limit: 0,
    }
  );
  const width = useWindowWidth();
  const isMobile = width < 768;

  const accountTypeOptions = [
    { id: 'Corrente', label: 'Corrente', icon: '🏦' },
    { id: 'Poupança', label: 'Poupança', icon: '🐖' },
    { id: 'Investimento', label: 'Invest.', icon: '📈' },
  ];

  const statusOptions = [
    { id: 'Ativa', label: 'Ativa', color: 'success' },
    { id: 'Inativa', label: 'Inativa', color: 'danger' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAccountTypeClick = (type) => {
    setFormData({ ...formData, account_type: type });
  };

  const handleStatusClick = (status) => {
    setFormData({ ...formData, status });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = bank ? 'put' : 'post';
    const url = bank ? `/banks/${bank.id}` : '/banks';

    api[method](url, formData)
      .then(() => {
        swalSuccess('Banco salvo com sucesso!');
        onSuccess();
      })
      .catch(error => {
        console.error('Houve um erro ao salvar o banco!', error);
        swalError('Erro ao salvar banco.');
      });
  };

  // ════════════════════════════════════
  // MOBILE FORM
  // ════════════════════════════════════
  const renderMobileForm = () => (
    <form onSubmit={handleSubmit} className="expense-form-mobile" autoComplete="off">

      {/* Tipo de Conta */}
      <div className="form-section">
        <label className="section-label">Tipo de Conta</label>
        <div className="payment-methods-grid">
          {accountTypeOptions.map(opt => (
            <button
              type="button"
              key={opt.id}
              className={`payment-method-btn ${formData.account_type === opt.id ? 'active' : ''}`}
              onClick={() => handleAccountTypeClick(opt.id)}
            >
              <span className="payment-icon">{opt.icon}</span>
              <span className="payment-label">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Nome */}
      <div className="form-section">
        <label className="section-label">Nome</label>
        <input
          type="text"
          className="form-control form-control-lg"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Ex: Banco do Brasil..."
          required
        />
      </div>

      {/* Código + Agência */}
      <div className="form-section">
        <div className="row g-2">
          <div className="col-6">
            <label className="section-label">Código</label>
            <input
              type="text"
              className="form-control"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="001"
              required
            />
          </div>
          <div className="col-6">
            <label className="section-label">Agência</label>
            <input
              type="text"
              className="form-control"
              name="agency"
              value={formData.agency}
              onChange={handleChange}
              placeholder="0001"
              required
            />
          </div>
        </div>
      </div>

      {/* Número da Conta */}
      <div className="form-section">
        <label className="section-label">Número da Conta</label>
        <input
          type="text"
          className="form-control"
          name="account_number"
          value={formData.account_number}
          onChange={handleChange}
          placeholder="00000-0"
          required
        />
      </div>

      {/* Saldo Inicial + Data Abertura */}
      <div className="form-section">
        <div className="row g-2">
          <div className="col-6">
            <label className="section-label">Saldo Inicial</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="initial_balance"
              value={formData.initial_balance}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-6">
            <label className="section-label">Data Abertura</label>
            <input
              type="date"
              className="form-control"
              name="opening_date"
              value={formData.opening_date}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      {/* Cheque Especial + Cor */}
      <div className="form-section">
        <div className="row g-2">
          <div className="col-7">
            <label className="section-label">Limite Cheque Especial</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="overdraft_limit"
              value={formData.overdraft_limit}
              onChange={handleChange}
            />
          </div>
          <div className="col-5">
            <label className="section-label">Cor</label>
            <input
              type="color"
              className="form-control form-control-color w-100"
              name="color"
              value={formData.color}
              onChange={handleChange}
              required
              style={{ height: '48px' }}
            />
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="form-section">
        <label className="section-label">Status</label>
        <div className="status-buttons">
          {statusOptions.map(s => (
            <button
              type="button"
              key={s.id}
              className={`status-btn ${s.color} ${formData.status === s.id ? 'active' : ''}`}
              onClick={() => handleStatusClick(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mobile-form-footer">
        <button type="submit" className="btn btn-primary btn-lg w-100">
          Salvar Banco
        </button>
      </div>
    </form>
  );

  // ════════════════════════════════════
  // DESKTOP FORM
  // ════════════════════════════════════
  const renderDesktopForm = () => (
    <form onSubmit={handleSubmit} className="rv-form-desktop">

      {/* Tipo de Conta */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Tipo de Conta</label>
        <div className="rv-payment-grid">
          {accountTypeOptions.map(opt => (
            <button
              type="button"
              key={opt.id}
              className={`rv-payment-btn ${formData.account_type === opt.id ? 'active' : ''}`}
              onClick={() => handleAccountTypeClick(opt.id)}
            >
              <span className="rv-pay-icon">{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Nome + Código + Agência */}
      <div className="rv-form-grid-3">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="name">Nome</label>
          <input type="text" className="rv-form-input" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Nome do banco" required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="code">Código</label>
          <input type="text" className="rv-form-input" id="code" name="code" value={formData.code} onChange={handleChange} placeholder="001" required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="agency">Agência</label>
          <input type="text" className="rv-form-input" id="agency" name="agency" value={formData.agency} onChange={handleChange} placeholder="0001" required />
        </div>
      </div>

      {/* Número da Conta + Saldo Inicial + Data de Abertura */}
      <div className="rv-form-grid-3">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="account_number">Número da Conta</label>
          <input type="text" className="rv-form-input" id="account_number" name="account_number" value={formData.account_number} onChange={handleChange} placeholder="00000-0" required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="initial_balance">Saldo Inicial (R$)</label>
          <input type="number" step="0.01" className="rv-form-input" id="initial_balance" name="initial_balance" value={formData.initial_balance} onChange={handleChange} required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="opening_date">Data de Abertura</label>
          <input type="date" className="rv-form-input" id="opening_date" name="opening_date" value={formData.opening_date} onChange={handleChange} required />
        </div>
      </div>

      {/* Limite Cheque Especial + Cor */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="overdraft_limit">Limite de Cheque Especial (R$)</label>
          <input type="number" step="0.01" className="rv-form-input" id="overdraft_limit" name="overdraft_limit" value={formData.overdraft_limit} onChange={handleChange} />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="color">Cor</label>
          <input type="color" className="rv-form-input" id="color" name="color" value={formData.color} onChange={handleChange} required style={{ height: '40px', padding: '0.25rem' }} />
        </div>
      </div>

      {/* Status */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Status</label>
        <div className="rv-status-group">
          {statusOptions.map(s => (
            <button
              type="button"
              key={s.id}
              className={`rv-status-toggle ${formData.status === s.id ? `active-${s.color}` : ''}`}
              onClick={() => handleStatusClick(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rv-form-footer">
        <button type="submit" className="rv-btn-submit">Salvar Banco</button>
      </div>
    </form>
  );

  return isMobile ? renderMobileForm() : renderDesktopForm();
}

export default BankForm;
